'use client'

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { sanitizeHref } from '@/lib/sanitize'
import {
  X,
  Mail,
  KeyRound,
  CheckCircle2,
  Loader2,
  Sparkles,
  ArrowRight,
} from 'lucide-react'
import { radarAnalytics } from '@/lib/analytics/radar-events'
import type { FreeToolSlug } from '@/lib/tool-access'

/**
 * useToolAccessGate — hook + JSX modal for the /tools/* free gate.
 *
 * Lazy: the modal does not render on mount. The tool page calls openGate()
 * from its submit handler when !ready. After a successful audit run, the
 * page must call markConsumed() so the next click re-opens the modal.
 *
 *   const gate = useToolAccessGate('crawl-check', 'AI Crawl Checker')
 *
 *   async function handleSubmit() {
 *     if (!gate.ready) return gate.openGate()
 *     // ...
 *     gate.markConsumed()
 *   }
 *
 *   return <>{gate.modal}{ ...existing JSX... }</>
 */

type Status =
  | 'idle'
  | 'email'
  | 'sending'
  | 'code'
  | 'verifying'
  | 'ready'
  | 'error'
  | 'capped'
  | 'checking'
  // Returning email that already spent its one free run. No form — just the
  // upsell. This is what closes the re-verify farming loop at the UI.
  | 'used'

/** Payload of the 429 domain_cap response from /api/tools/* routes. */
export interface DomainCapInfo {
  domain: string
  message: string
  cachedResultUrl: string | null
  upsellUrl: string
}

interface UpsellPayload {
  trigger: 'repeat_audits'
  priorRequests: number
  cta: 'pro_retainer'
  ctaUrl: string
  headline: string
  body: string
}

export interface ToolGate {
  ready: boolean
  email: string | null
  isPaid: boolean
  openGate: (target?: string) => void
  markConsumed: () => void
  showDomainCap: (info: DomainCapInfo) => void
  modal: ReactNode
}

export function useToolAccessGate(
  toolSlug: FreeToolSlug,
  toolName: string
): ToolGate {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState<Status>('idle')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [isPaid, setIsPaid] = useState(false)
  const [upsell, setUpsell] = useState<UpsellPayload | null>(null)
  const [upsellDismissed, setUpsellDismissed] = useState(false)
  const [capInfo, setCapInfo] = useState<DomainCapInfo | null>(null)
  // Server ran the paid-user sign-in recovery for this email (uniform flag —
  // an email went out only if the account actually qualifies).
  const [recoveryInitiated, setRecoveryInitiated] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function init() {
      try {
        const res = await fetch(
          `/api/tools/access/me?tool=${encodeURIComponent(toolSlug)}`,
          { cache: 'no-store' }
        )
        const data = await res.json().catch(() => ({}))
        if (cancelled) return
        if (data?.isPaid) {
          setIsPaid(true)
          setReady(true)
          return
        }
        if (data?.hasCredit) {
          setReady(true)
          return
        }
        const verified =
          data?.verifiedEmail && typeof data.verifiedEmail === 'string'
            ? data.verifiedEmail
            : ''
        if (verified) {
          // Already verified once + no active credit = free run spent.
          // Show the upsell, not another verify form (that loop is the abuse).
          setEmail(verified)
          setStatus('used')
          setOpen(true)
          radarAnalytics.toolAccessGateViewed(toolSlug)
          return
        }
        // New visitor — open the email lead-capture form for their one free run.
        setStatus('email')
        setOpen(true)
        radarAnalytics.toolAccessGateViewed(toolSlug)
      } catch {
        // Non-fatal — gate just opens on submit.
      }
    }
    void init()
    return () => {
      cancelled = true
    }
  }, [toolSlug])

  // When the page passes the typed URL/brand, pre-check the domain cap so
  // an already-capped domain jumps straight to the upsell dialog instead
  // of collecting an email verification that would be blocked at run time.
  // Advisory only (fails open to the email step) — the tool route enforces.
  const openGate = useCallback(
    (target?: string) => {
      if (ready) return
      setOpen(true)
      radarAnalytics.toolAccessGateViewed(toolSlug)
      setErrorMsg(null)
      // Returning email already used its free run → upsell, no form, no checks.
      if (email) {
        setStatus('used')
        return
      }
      if (!target) {
        setStatus('email')
        return
      }
      setStatus('checking')
      void (async () => {
        try {
          const res = await fetch(
            `/api/tools/access/cap-check?tool=${encodeURIComponent(toolSlug)}&target=${encodeURIComponent(target)}`,
            { cache: 'no-store' }
          )
          const data = await res.json().catch(() => ({}))
          if (data?.capped) {
            setCapInfo(data as DomainCapInfo)
            setStatus('capped')
            return
          }
        } catch {
          // Fail open — run-time check still enforces.
        }
        setStatus('email')
      })()
    },
    [ready, email, toolSlug]
  )

  const markConsumed = useCallback(() => {
    if (isPaid) return
    setReady(false)
    setCode('')
    setStatus('idle')
    radarAnalytics.toolAccessGateRunConsumed(toolSlug)
  }, [isPaid, toolSlug])

  const close = useCallback(() => {
    if (status === 'sending' || status === 'verifying') return
    setOpen(false)
    setErrorMsg(null)
  }, [status])

  // Domain hit its free-run cap (429 domain_cap from the tool route).
  // Shows the cap explanation + Radar upsell INSTEAD of the email/OTP
  // form — re-verifying with another email cannot unlock a capped domain.
  const showDomainCap = useCallback(
    (info: DomainCapInfo) => {
      setCapInfo(info)
      setStatus('capped')
      setOpen(true)
      radarAnalytics.toolAccessGateViewed(toolSlug)
    },
    [toolSlug]
  )

  const requestCode = useCallback(async () => {
    if (!email.trim()) return
    setStatus('sending')
    setErrorMsg(null)
    try {
      const res = await fetch('/api/tools/access/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), tool: toolSlug }),
      })
      const data = await res.json().catch(() => ({}))
      // Email already used its one free test — go straight to the upsell,
      // no OTP, no error styling.
      if (data?.reason === 'already_used') {
        setRecoveryInitiated(Boolean(data?.recoveryInitiated))
        setStatus('used')
        return
      }
      if (!res.ok) {
        setErrorMsg(friendlyError(data?.reason))
        setStatus('error')
        return
      }
      if (data?.bypass) {
        setReady(true)
        setOpen(false)
        setStatus('idle')
        radarAnalytics.toolAccessGateGranted(toolSlug, 'bypass')
        return
      }
      // Pro Retainer upsell may be attached when the email has hit the
      // repeat-audit threshold. Surfaced as a dismissable card above the
      // OTP form. Never blocks the OTP flow.
      if (data?.upsell) {
        setUpsell(data.upsell as UpsellPayload)
        setUpsellDismissed(false)
      }
      setStatus('code')
      radarAnalytics.toolAccessGateCodeRequested(toolSlug)
    } catch {
      setErrorMsg('Network error. Try again.')
      setStatus('error')
    }
  }, [email, toolSlug])

  const submitCode = useCallback(async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      setErrorMsg('Enter the 6-digit code from the email.')
      return
    }
    setStatus('verifying')
    setErrorMsg(null)
    try {
      const res = await fetch('/api/tools/access/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          code: code.trim(),
          tool: toolSlug,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.ok) {
        setErrorMsg(friendlyError(data?.reason))
        setStatus('code')
        return
      }
      setReady(true)
      setOpen(false)
      setStatus('idle')
      radarAnalytics.toolAccessGateGranted(toolSlug, 'otp')
    } catch {
      setErrorMsg('Network error. Try again.')
      setStatus('code')
    }
  }, [code, email, toolSlug])

  const modal = useMemo(() => {
    if (!open) return null
    return (
      <div
        className='fixed inset-0 z-50 flex items-center justify-center p-4'
        style={{ background: 'rgba(0,0,0,0.7)' }}
        onClick={close}
      >
        <div
          className='w-full max-w-md rounded-2xl border p-8 bg-card border-border'
          onClick={e => e.stopPropagation()}
        >
          <div className='flex items-start justify-between mb-6'>
            <div>
              <h3 className='text-xl font-bold text-foreground mb-1'>
                {status === 'capped'
                  ? 'Free audits used for this domain'
                  : status === 'used'
                    ? "You've used your free audit"
                    : status === 'code' || status === 'verifying'
                      ? 'Enter your verification code'
                      : `Run ${toolName}`}
              </h3>
              <p className='text-sm text-muted-foreground'>
                {status === 'capped'
                  ? `${capInfo?.domain ?? 'This domain'} has reached its free audit limit for the last 30 days.`
                  : status === 'used'
                    ? 'Upgrade to Pro for unlimited audits across every tool.'
                    : status === 'code' || status === 'verifying'
                      ? `We sent a 6-digit code to ${email}. It expires in 15 minutes.`
                      : 'One free audit per email. Result delivered in-page.'}
              </p>
            </div>
            <button
              type='button'
              onClick={close}
              className='p-1 rounded hover:bg-muted transition-colors'
              aria-label='Close'
            >
              <X size={18} className='text-muted-foreground' />
            </button>
          </div>

          {status === 'checking' && (
            <div
              className='flex items-center justify-center py-10'
              role='status'
              aria-label='Checking availability'
            >
              <Loader2
                size={20}
                className='animate-spin text-muted-foreground'
              />
            </div>
          )}

          {status === 'used' && (
            <div className='space-y-3'>
              {recoveryInitiated && (
                <p className='text-sm text-foreground/80'>
                  If this email has Radar credits, purchases, or an active
                  subscription, we&apos;ll send a sign-in link. Please check
                  your inbox and spam folder.
                </p>
              )}
              <a
                href='/platform#pricing'
                target='_blank'
                rel='noopener noreferrer'
                className='w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110'
                style={{
                  background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
                  color: '#fff',
                }}
              >
                Need more audits? Upgrade to Pro
                <ArrowRight size={14} />
              </a>
              <p className='text-xs text-muted-foreground text-center'>
                Already purchased?{' '}
                <a
                  href='/platform/sign-in'
                  className='font-medium hover:underline'
                  style={{ color: '#008dcc' }}
                >
                  Sign in
                </a>
              </p>
            </div>
          )}

          {status === 'capped' && capInfo && (
            <div className='space-y-3'>
              <div
                className='rounded-xl border p-4'
                style={{
                  borderColor: 'rgba(139, 92, 246, 0.3)',
                  background:
                    'linear-gradient(135deg, rgba(0, 141, 204, 0.06), rgba(139, 92, 246, 0.06))',
                }}
              >
                <div className='flex items-start gap-2 mb-2'>
                  <Sparkles
                    size={14}
                    className='mt-0.5 flex-shrink-0'
                    style={{ color: '#8b5cf6' }}
                  />
                  <p className='text-sm font-semibold text-foreground'>
                    Keep auditing with Pixelmojo Radar
                  </p>
                </div>
                <p className='text-[12px] text-muted-foreground leading-relaxed'>
                  Free tools give every domain a one-time snapshot. Radar gives
                  you unlimited re-runs, all tools, and fix tracking over time
                  so you can verify every change actually moved your score.
                </p>
              </div>
              <a
                href={sanitizeHref(capInfo.upsellUrl) ?? '/platform#pricing'}
                className='w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110'
                style={{
                  background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
                  color: '#fff',
                }}
              >
                See Radar plans
                <ArrowRight size={14} />
              </a>
              {capInfo.cachedResultUrl && (
                <a
                  href={sanitizeHref(capInfo.cachedResultUrl) ?? '/platform'}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='block text-center text-[12px] font-semibold transition-colors'
                  style={{ color: '#008dcc' }}
                >
                  View the existing report for {capInfo.domain}
                </a>
              )}
            </div>
          )}

          {(status === 'email' ||
            status === 'sending' ||
            (status === 'error' && !code)) && (
            <form
              onSubmit={e => {
                e.preventDefault()
                void requestCode()
              }}
              className='space-y-3'
            >
              <div className='relative'>
                <Mail
                  size={14}
                  className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                />
                <input
                  type='email'
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value)
                    if (errorMsg) setErrorMsg(null)
                  }}
                  placeholder='your@company.com'
                  required
                  autoFocus
                  disabled={status === 'sending'}
                  className='w-full h-11 pl-9 pr-3 rounded-lg text-sm outline-none transition-colors focus:ring-1'
                  style={{
                    background: 'var(--background)',
                    border: '1px solid var(--control-border)',
                    color: 'var(--foreground)',
                  }}
                />
              </div>
              <button
                type='submit'
                disabled={status === 'sending' || !email.trim()}
                className='w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-40'
                style={{
                  background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
                  color: '#fff',
                }}
              >
                {status === 'sending' ? (
                  <>
                    <Loader2 size={14} className='animate-spin' /> Sending…
                  </>
                ) : (
                  'Send my code'
                )}
              </button>
              {errorMsg && (
                <p className='text-[11px]' style={{ color: '#F90B8A' }}>
                  {errorMsg}
                </p>
              )}
              <p className='text-[11px] text-center text-muted-foreground mt-4'>
                We use your email to send results and the occasional product
                update. Unsubscribe any time.
              </p>
            </form>
          )}

          {(status === 'code' ||
            status === 'verifying' ||
            (status === 'error' && code)) &&
            upsell &&
            !upsellDismissed && (
              <div
                className='mb-4 rounded-xl border p-4'
                style={{
                  borderColor: 'rgba(139, 92, 246, 0.3)',
                  background:
                    'linear-gradient(135deg, rgba(0, 141, 204, 0.06), rgba(139, 92, 246, 0.06))',
                }}
              >
                <div className='flex items-start gap-2 mb-2'>
                  <Sparkles
                    size={14}
                    className='mt-0.5 flex-shrink-0'
                    style={{ color: '#8b5cf6' }}
                  />
                  <p className='text-sm font-semibold text-foreground'>
                    {upsell.headline}
                  </p>
                </div>
                <p className='text-[12px] text-muted-foreground leading-relaxed mb-3'>
                  {upsell.body}
                </p>
                <div className='flex items-center justify-between gap-2'>
                  <a
                    href={upsell.ctaUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors'
                    style={{ color: '#008dcc' }}
                  >
                    See Pro Retainer
                    <ArrowRight size={12} />
                  </a>
                  <button
                    type='button'
                    onClick={() => setUpsellDismissed(true)}
                    className='text-[11px] text-muted-foreground hover:text-foreground transition-colors'
                  >
                    Not now
                  </button>
                </div>
              </div>
            )}

          {(status === 'code' ||
            status === 'verifying' ||
            (status === 'error' && code)) && (
            <form
              onSubmit={e => {
                e.preventDefault()
                void submitCode()
              }}
              className='space-y-3'
            >
              <div className='relative'>
                <KeyRound
                  size={14}
                  className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                />
                <input
                  type='text'
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  pattern='[0-9]{6}'
                  maxLength={6}
                  value={code}
                  onChange={e => {
                    setCode(e.target.value.replace(/\D/g, ''))
                    if (errorMsg) setErrorMsg(null)
                  }}
                  placeholder='123456'
                  required
                  autoFocus
                  disabled={status === 'verifying'}
                  className='w-full h-11 pl-9 pr-3 rounded-lg text-sm outline-none transition-colors focus:ring-1 tracking-[6px] font-mono'
                  style={{
                    background: 'var(--background)',
                    border: '1px solid var(--control-border)',
                    color: 'var(--foreground)',
                  }}
                />
              </div>
              <button
                type='submit'
                disabled={
                  status === 'verifying' || !/^\d{6}$/.test(code.trim())
                }
                className='w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-40'
                style={{
                  background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
                  color: '#fff',
                }}
              >
                {status === 'verifying' ? (
                  <>
                    <Loader2 size={14} className='animate-spin' /> Verifying…
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={14} /> Verify and run audit
                  </>
                )}
              </button>
              {errorMsg && (
                <p className='text-[11px]' style={{ color: '#F90B8A' }}>
                  {errorMsg}
                </p>
              )}
              <div className='flex items-center justify-end text-[11px] pt-2'>
                <a
                  href='/platform#pricing'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-semibold transition-colors hover:brightness-110'
                  style={{ color: '#8b5cf6' }}
                >
                  Need more audits? Upgrade to Pro →
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    )
  }, [
    open,
    status,
    toolName,
    email,
    code,
    errorMsg,
    upsell,
    upsellDismissed,
    capInfo,
    recoveryInitiated,
    close,
    requestCode,
    submitCode,
  ])

  return {
    ready,
    email: email || null,
    isPaid,
    openGate,
    markConsumed,
    showDomainCap,
    modal,
  }
}

function friendlyError(reason: unknown): string {
  switch (reason) {
    case 'invalid':
      return 'That code is incorrect. Check your email and try again.'
    case 'expired':
      return 'That code expired. Request a new one.'
    case 'too_many_attempts':
      return 'Too many wrong attempts. Request a new code.'
    case 'rate_limit':
      return 'Too many requests. Wait a minute and try again.'
    case 'daily_cap':
      return 'You have hit the daily verification limit for this email.'
    case 'already_used':
      return 'This email already used its free run. Upgrade to Pro for unlimited audits.'
    case 'invalid_email':
      return 'That email looks invalid. Try another.'
    case 'disposable_email':
      return 'Temporary email addresses are not supported. Use your real work or personal email.'
    case 'invalid_tool':
      return 'Unsupported tool. Refresh the page.'
    case 'server_error':
      return 'Our server hit a snag. Try again in a moment.'
    default:
      return 'Something went wrong. Try again.'
  }
}
